import React, { useState, useEffect } from 'react'
import { Button, Card, Col, Space, Steps, message, theme } from 'antd'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCheckCircle, faChevronLeft, faChevronRight, faXmarkCircle } from '@fortawesome/free-solid-svg-icons'
import CustomRow from '../../../reusable-components/CustomRow'
import { useDispatch } from 'react-redux'
import CoreEnums from '../../../../core/utils/enums'
import coreReducer from '../../../../core/utils/reducer'
import { createCarePlanTemplate, updateCarePlanTemplate } from '../../utils/utils'
import { handleError } from '../../../lib/utils'

// Import Common Steps
import GeneralForm from '../../Common/WizardSteps/GeneralForm'
import VisitTrackers from '../../Common/WizardSteps/VisitTrackers'
import Summary from '../../Common/WizardSteps/Summary'

// Import Custom Wellness Steps
import NutritionManagement from './NutritionManagement'
import PhysicalActivityManagement from './PhysicalActivityManagement'
import StressManagement from './StressManagement'
import SleepManagement from './SleepManagement'
import WellnessGoals from './WellnessGoals'
import WellnessEducation from './WellnessEducation'

// Registry for tab keys by plan type
const getPlanTabKey = (planType, isMonitoring) => {
  const planTabKeys = {
    care: isMonitoring ? CoreEnums.tabKeys.MONITORING_PLANS : CoreEnums.tabKeys.CARE_PLANS,
    treatment: 'TREATMENT_PLANS',
    nutrition: 'NUTRITION_PLANS',
    wellness: 'WELLNESS_PLANS',
    preventive: 'PREVENTIVE_PLANS',
    rehabilitation: 'REHAB_PLANS',
    weight: 'WEIGHT_PLANS'
  }

  return planTabKeys[planType] || CoreEnums.tabKeys.CARE_PLANS
}

const PlanTemplateForm = ({
  isNew,
  recordData,
  dataPoints,
  refreshView,
  isMonitoring,
  planType = 'wellness',
  planLabel = 'Wellness Plans'
}) => {
  const dispatch = useDispatch()
  const { token } = theme.useToken()

  // Step state
  const [currentStep, setCurrentStep] = useState(0)

  // All state initialized with empty values
  const [general, setGeneral] = useState({ name: '', description: '' })
  const [visits, setVisits] = useState([])
  const [nutrition, setNutrition] = useState([])
  const [physicalActivities, setPhysicalActivities] = useState([])
  const [stressManagement, setStressManagement] = useState([])
  const [sleepManagement, setSleepManagement] = useState([])
  const [wellnessGoals, setWellnessGoals] = useState([])
  const [educationalResources, setEducationalResources] = useState([])

  // Load data once on component mount
  useEffect(() => {
    // Only try to load data if editing an existing record
    if (!isNew && recordData) {
      // General information
      if (recordData.name) {
        setGeneral({
          name: recordData.name || '',
          description: recordData.description || ''
        })
      }

      // Load visits (safe way with many null checks)
      try {
        if (Array.isArray(recordData.tracking)) {
          const visitItems = recordData.tracking.filter((item) => item && item.trackerType === 'visits')
          setVisits(visitItems)
        }
      } catch (err) {
        console.error('Error loading visits:', err)
        // Fail safely with empty array
        setVisits([])
      }

      // Load other sections (with safe fallbacks to empty arrays)
      if (Array.isArray(recordData.nutrition)) setNutrition(recordData.nutrition)
      if (Array.isArray(recordData.physicalActivities)) setPhysicalActivities(recordData.physicalActivities)
      if (Array.isArray(recordData.stressManagement)) setStressManagement(recordData.stressManagement)
      if (Array.isArray(recordData.sleepManagement)) setSleepManagement(recordData.sleepManagement)
      if (Array.isArray(recordData.wellnessGoals)) setWellnessGoals(recordData.wellnessGoals)
      if (Array.isArray(recordData.educationalResources)) setEducationalResources(recordData.educationalResources)
    }
  }, [isNew, recordData])

  const checkGeneralStatus = () => {
    return general.name && general.name.length > 0
  }

  const handleFinalizePlan = async () => {
    try {
      const plan = {
        name: general.name,
        description: general.description,
        planType: 'wellness',
        tracking: [...visits],
        nutrition,
        physicalActivities,
        stressManagement,
        sleepManagement,
        wellnessGoals,
        educationalResources
      }

      if (isNew) {
        await createCarePlanTemplate(plan)
        message.success('Wellness Plan created successfully')
      } else {
        await updateCarePlanTemplate(recordData._id, {
          ...plan,
          _id: recordData._id
        })
        message.success('Wellness Plan updated successfully')
      }

      closeTab()
      refreshView()
    } catch (error) {
      message.error(handleError(error))
    }
  }

  const closeTab = () => {
    const tabKey = getPlanTabKey('wellness', isMonitoring)
    // Safe access with optional chaining and fallback
    const tabId = isNew ? `${tabKey}_new` : `${tabKey}_${recordData?._id || 'unknown'}`
    dispatch(coreReducer.actions.closeTab(tabId))
  }

  return (
    <CustomRow>
      <Col span={24}>
        <Card
          size='small'
          style={{ marginBottom: 12 }}
          title={
            <Space>
              <span>{isNew ? 'Create New Wellness Plan' : 'Edit Wellness Plan'}</span>
              <Button
                danger
                onClick={() => {
                  closeTab()
                }}
              >
                <FontAwesomeIcon icon={faXmarkCircle} />
              </Button>
            </Space>
          }
        >
          <Steps
            current={currentStep}
            onChange={(e) => {
              setCurrentStep(e)
            }}
            items={[
              {
                title: 'General',
                status: checkGeneralStatus() ? 'finish' : 'error'
              },
              {
                title: 'Wellness Visits & Checkups',
                status: visits.length > 0 ? 'finish' : 'wait'
              },
              {
                title: 'Nutrition & Dietary Planning',
                status: nutrition.length > 0 ? 'finish' : 'wait'
              },
              {
                title: 'Exercise & Physical Activity',
                status: physicalActivities.length > 0 ? 'finish' : 'wait'
              },
              {
                title: 'Stress & Mental Wellness',
                status: stressManagement.length > 0 ? 'finish' : 'wait'
              },
              {
                title: 'Sleep & Recovery Tracking',
                status: sleepManagement.length > 0 ? 'finish' : 'wait'
              },
              {
                title: 'Personal Wellness Goals',
                status: wellnessGoals.length > 0 ? 'finish' : 'wait'
              },
              {
                title: 'Educational Resources',
                status: educationalResources.length > 0 ? 'finish' : 'wait'
              },
              {
                title: 'Summary',
                status: 'wait'
              }
            ]}
          />
        </Card>
      </Col>
      <Col span={24}>
        <Card
          size='small'
          extra={
            <Space>
              {currentStep !== 0 ? (
                <Button
                  type='primary'
                  onClick={() => {
                    setCurrentStep(currentStep - 1)
                  }}
                  style={{ marginRight: 8 }}
                >
                  <FontAwesomeIcon icon={faChevronLeft} />
                  Previous
                </Button>
              ) : undefined}
              {currentStep !== 8 ? (
                <Button
                  disabled={currentStep === 0 && !checkGeneralStatus()}
                  type='primary'
                  onClick={() => {
                    setCurrentStep(currentStep + 1)
                  }}
                >
                  Next
                  <FontAwesomeIcon icon={faChevronRight} />
                </Button>
              ) : (
                <Button
                  type='primary'
                  style={{ background: token.colorSuccess }}
                  onClick={() => {
                    handleFinalizePlan()
                  }}
                >
                  Finalize
                  <FontAwesomeIcon icon={faCheckCircle} />
                </Button>
              )}
            </Space>
          }
        >
          {currentStep === 0 && (
            <GeneralForm
              isMonitoring={isMonitoring}
              data={general}
              setData={setGeneral}
              planType='wellness'
              planLabel='Wellness Plans'
            />
          )}
          {currentStep === 1 && (
            <VisitTrackers
              handleAddTracking={(key, selection) => {
                setVisits([...visits, selection])
              }}
              visits={visits}
              handleUpdateTracker={(value, index, property) => {
                const newVisits = [...visits]
                newVisits[index][property] = value
                setVisits(newVisits)
              }}
              handleRemoveTracker={(index) => {
                const newVisits = [...visits]
                newVisits.splice(index, 1)
                setVisits(newVisits)
              }}
              planType='wellness'
              planLabel='Wellness Plans'
            />
          )}
          {currentStep === 2 && <NutritionManagement nutrition={nutrition} setNutrition={setNutrition} />}
          {currentStep === 3 && (
            <PhysicalActivityManagement
              physicalActivities={physicalActivities}
              setPhysicalActivities={setPhysicalActivities}
            />
          )}
          {currentStep === 4 && (
            <StressManagement stressManagement={stressManagement} setStressManagement={setStressManagement} />
          )}
          {currentStep === 5 && (
            <SleepManagement sleepManagement={sleepManagement} setSleepManagement={setSleepManagement} />
          )}
          {currentStep === 6 && <WellnessGoals wellnessGoals={wellnessGoals} setWellnessGoals={setWellnessGoals} />}
          {currentStep === 7 && (
            <WellnessEducation
              educationalResources={educationalResources}
              setEducationalResources={setEducationalResources}
            />
          )}
          {currentStep === 8 && (
            <Summary
              general={general}
              visits={visits}
              isMonitoring={isMonitoring}
              setCurrentStep={setCurrentStep}
              planType='wellness'
              planLabel='Wellness Plans'
              additionalSections={[
                { title: 'Nutrition & Dietary Planning', data: nutrition },
                { title: 'Exercise & Physical Activity', data: physicalActivities },
                { title: 'Stress & Mental Wellness', data: stressManagement },
                { title: 'Sleep & Recovery Tracking', data: sleepManagement },
                { title: 'Personal Wellness Goals', data: wellnessGoals },
                { title: 'Educational Resources', data: educationalResources }
              ]}
            />
          )}
        </Card>
      </Col>
    </CustomRow>
  )
}

export default PlanTemplateForm
